import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { FormattedMessage } from 'react-intl';

import TestResultsHeader from '../../../components/TestResultsHeader';
import ReportBox from '../../../components/ReportBox';
import Divider from '../../../components/Divider';
import LabResults from '../LabResults';
import TestResultsNavigation from '../TestResultsNavigation';
import RecommendedActions from '../RecommendedActions';
import TestResultsDocuments from '../TestResultsDocuments';
import SideDrawer from '../../../components/SideDrawer';
import TextWithAccordionListItemsCard from '../../../components/TextWithAccordionListItemsCard';
import TextBannerWithIcon from '../../../components/TextBannerWithIcon';
import TextBannerLarge from '../../../components/TextBannerLarge';

import ImportantIcon from '../../../assets/storybook-assets/images/error-icon-24.png';

import useScrollHandler from './useScrollHandler.hook';
import formatDate from '../../helpers/dateFormat';

import styles from './index.module.scss';

const TestResultsForTestosteroneCheck = ({ data, language }) => {
  const {
    title,
    description,
    labResults,
    overallResult,
    documents,
    recommendedActions,
    infoBlock,
    resultsAvailableDate,
    infoAccordions,
  } = data;

  const { understandingResults, takingAction } = infoAccordions;

  const { scrollableElementRef, linksData, activeSection } = useScrollHandler({
    data,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const {
    labReport,
    reimbursementReceipt,
  } = documents;

  const { testosteroneLevels, testosteroneDecline } = infoBlock;

  const isAnyDocumentsAvailable =
    labReport ||
    reimbursementReceipt;

  const handleOpenModal = (info) => {
    setModalContent(<Markdown rehypePlugins={[rehypeRaw]}>{info}</Markdown>);
    setIsModalOpen(true);
  };
  const formattedResultsAvailableDate = formatDate(resultsAvailableDate);

  return (
    <div ref={scrollableElementRef} className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <TestResultsHeader
            className={styles.testResultsHeader}
            title={title}
            overallResult={overallResult}
            resultsAvailableDate={formattedResultsAvailableDate}
          />
          <div className={styles.navigationMobileWrapper}>
            <TestResultsNavigation
              className={styles.navigationMobile}
              linksData={linksData}
              activeSection={activeSection}
              scrollableElementRef={scrollableElementRef}
            />
          </div>
          {description && (
            <div id='summary' className={styles.summary}>
              <Divider
                title={
                  <FormattedMessage id='app.summary' defaultMessage='Summary' />
                }
              />
              <ReportBox className={styles.reportBox} text={description} />
            </div>
          )}

          {labResults && labResults.length > 0 && (
            <div id='lab' className={styles.labResultsWrapper}>
              <Divider
                title={
                  <FormattedMessage
                    id='test-result.lab-results'
                    defaultMessage='Lab results'
                  />
                }
              />
              <div className={styles.labResults}>
                <LabResults
                  labResults={labResults}
                  type={data.type}
                  handleOpenModal={handleOpenModal}
                />
              </div>
            </div>
          )}

          {isAnyDocumentsAvailable && (
            <div className={styles.documentsWrapperMobile}>
              <TestResultsDocuments documents={documents} />
            </div>
          )}

          {infoBlock && testosteroneLevels && (
            <div className={styles.infoBlock}>
              <TextBannerWithIcon
                text={testosteroneLevels.text}
                iconPosition={testosteroneLevels.iconPosition}
                backgroundColor={testosteroneLevels.backgroundColor}
                iconUrl={ImportantIcon}
                iconAlt='Important'
              />
            </div>
          )}

          {infoAccordions && understandingResults && (
            <div className={styles.infoAccordions}>
              <TextWithAccordionListItemsCard
                textBlock={understandingResults.textBlock}
                accordionListItems={understandingResults.accordionListItems}
              />
            </div>
          )}

          {infoBlock && testosteroneDecline && (
            <div className={styles.infoBlock}>
              <TextBannerLarge
                title={testosteroneDecline.title}
                text={testosteroneDecline.text}
                backgroundColor={testosteroneDecline.backgroundColor}
              />
            </div>
          )}

          {infoAccordions && takingAction && (
            <div className={styles.infoAccordions}>
              <TextWithAccordionListItemsCard
                textBlock={takingAction.textBlock}
                accordionListItems={takingAction.accordionListItems}
              />
            </div>
          )}

          {recommendedActions && (
            <div id='actions' className={styles.actions}>
              <Divider
                title={
                  <FormattedMessage
                    id='app.recommended-actions'
                    defaultMessage='Recommended actions'
                  />
                }
              />
              <div className={styles.recommendedActions}>
                {recommendedActions.map((action, index) => {
                  const isLastRecommendedActionsBlock =
                    index === recommendedActions.length - 1;

                  return (
                    <RecommendedActions
                      key={index}
                      timing={action.timing}
                      actions={action.actions}
                      labReport={documents.labReport}
                      isLastRecommendedActionsBlock={
                        isLastRecommendedActionsBlock
                      }
                      handleInfo={(infoData) => handleOpenModal(infoData)}
                      language={language}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className={styles.sidebarWrapper}>
          <div className={styles.sidebar}>
            <TestResultsNavigation
              className={styles.navigationDesktop}
              linksData={linksData}
              activeSection={activeSection}
            />
            <TestResultsDocuments documents={documents} />
          </div>
        </div>
      </div>
      <SideDrawer onClick={() => setIsModalOpen(false)} isOpen={isModalOpen}>
        <div>{modalContent}</div>
      </SideDrawer>
    </div>
  );
};

TestResultsForTestosteroneCheck.propTypes = {
  data: PropTypes.object,
  language: PropTypes.string,
};

export default TestResultsForTestosteroneCheck;
